/**
 * Incoming buddy live-call detector.
 *
 * Polls /api/buddies/workout-invites every ~5s while the user is authed and
 * surfaces the most-recent unanswered "live" invite via a fullscreen ringtone
 * modal (see IncomingCallModal). This is the in-app companion to the Web Push
 * we fire from /buddies/live-invite — together they make sure the recipient
 * notices the call regardless of whether the app is open or backgrounded.
 *
 * NOTE: We deliberately don't piggyback on PresenceContext (30s) or
 * NotificationBell's poll (15s) — a phone call needs a tighter loop.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from './AuthContext';
import { useLiveVideo } from './LiveVideoContext';
import { useWorkoutStatus } from './WorkoutStatusContext';

const POLL_MS = 5000;
const FRESH_WINDOW_MS = 90 * 1000; // ignore invites older than 90s — dead rings

const IncomingCallContext = createContext({ activeInvite: null });

export function IncomingCallProvider({ children }) {
  const { user, api } = useAuth();
  const live = useLiveVideo();
  const { isWorkoutActive } = useWorkoutStatus();
  const [activeInvite, setActiveInvite] = useState(null);
  const dismissedRef = useRef(new Set()); // session ids we've manually dismissed
  const autoDeclinedRef = useRef(new Set()); // ids we've already auto-declined to avoid double-sends

  const poll = useCallback(async () => {
    try {
      const res = await api().get('/buddies/workout-invites');
      const received = res.data?.received || [];
      const now = Date.now();
      // Pick the freshest "live" invite where my participation is still pending.
      const candidate = received
        .filter((s) => {
          if (s.workout_id !== 'live' && s.kind !== 'live') return false;
          if (s.status !== 'pending') return false;
          if (dismissedRef.current.has(s.id)) return false;
          // Don't ring myself if I'm already in a session (probably the same one)
          if (live?.session?.sessionId === s.id) return false;
          const me = (s.participants || []).find((p) => p.user_id === user?.id);
          if (!me || me.status !== 'pending') return false;
          if (s.created_at) {
            const age = now - new Date(s.created_at).getTime();
            if (age > FRESH_WINDOW_MS) return false;
          }
          return true;
        })
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];

      // Auto-decline incoming calls when the user is mid-workout. We:
      //   1) POST /decline so the caller's UI flips status -> declined
      //   2) Send a polite DM "Busy finishing my set" so the caller knows why
      //   3) Mark the id as dismissed so the modal never opens
      // This way the user keeps lifting; the buddy gets immediate context.
      if (candidate && isWorkoutActive && !autoDeclinedRef.current.has(candidate.id)) {
        autoDeclinedRef.current.add(candidate.id);
        dismissedRef.current.add(candidate.id);
        const callerId = candidate.created_by;
        const callerName = (candidate.participants || []).find((p) => p.user_id === callerId)?.name || 'Your buddy';
        // Best-effort decline + DM. Don't await in series — fire both and move on.
        api().post(`/buddies/workout-invite/${candidate.id}/decline`).catch(() => {});
        if (callerId) {
          api().post(`/direct-messages/send/${callerId}`, {
            content: '🏋️ Busy finishing my set — call you right back!',
            message_type: 'text',
          }).catch(() => {});
        }
        toast.info(`Auto-declined call from ${callerName} — you're mid-workout`, { icon: '🏋️' });
        setActiveInvite(null);
        return;
      }

      setActiveInvite(candidate || null);
    } catch {
      /* network blip — try again next tick */
    }
  }, [api, user?.id, live?.session?.sessionId, isWorkoutActive]);

  useEffect(() => {
    if (!user) {
      setActiveInvite(null);
      dismissedRef.current.clear();
      return undefined;
    }
    poll();
    const t = setInterval(poll, POLL_MS);
    const onFocus = () => poll();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(t);
      window.removeEventListener('focus', onFocus);
    };
  }, [user, poll]);

  const dismissInvite = useCallback((inviteId) => {
    if (inviteId) dismissedRef.current.add(inviteId);
    setActiveInvite(null);
  }, []);

  return (
    <IncomingCallContext.Provider value={{ activeInvite, dismissInvite, refresh: poll }}>
      {children}
    </IncomingCallContext.Provider>
  );
}

export function useIncomingCall() {
  return useContext(IncomingCallContext);
}
